import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Phone } from "lucide-react";
import { CLINIC, whatsappLink } from "@/lib/clinic";

export default function Hero() {
    return (
        <section
            id="hero"
            className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden"
            data-testid="hero-section"
        >
            {/* Organic background shapes */}
            <div className="absolute top-24 -left-24 w-96 h-96 rounded-full bg-[#8A9A5B]/10 blur-3xl -z-0" />
            <div className="absolute bottom-0 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#5E7C88]/10 blur-3xl -z-0" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-7 flex flex-col gap-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="inline-flex items-center gap-2 self-start rounded-full bg-[#F3F1EC] px-4 py-2 border border-[#E8E6E1]"
                            data-testid="hero-badge"
                        >
                            <Sparkles className="w-3.5 h-3.5 text-[#8A9A5B]" />
                            <span className="text-xs uppercase tracking-[0.2em] text-[#4A5D54]">
                                Amravati · Dermatology & Dental
                            </span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            className="font-display text-4xl sm:text-5xl lg:text-7xl font-light text-[#1A221F] leading-[1.05] tracking-tight"
                            data-testid="hero-title"
                        >
                            Revitalizing <em className="italic text-[#3B5249] font-normal">skin.</em>
                            <br />
                            Perfecting <em className="italic text-[#8A9A5B] font-normal">smiles.</em>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="text-base md:text-lg text-[#4A5D54] leading-relaxed max-w-xl"
                            data-testid="hero-description"
                        >
                            A calm, modern clinic in the heart of Amravati dedicated to
                            advanced dermatology and comprehensive dental care. Evidence-based
                            treatments. Personal, unhurried attention.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="flex flex-wrap items-center gap-3"
                        >
                            <a
                                href={whatsappLink()}
                                target="_blank"
                                rel="noreferrer"
                                className="group inline-flex items-center gap-2 rounded-full bg-[#3B5249] text-white px-7 py-3.5 text-sm font-medium hover:bg-[#2C3E37] transition-all hover:scale-105 shadow-[0_10px_30px_rgba(59,82,73,0.25)]"
                                data-testid="hero-book-button"
                            >
                                Book on WhatsApp
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </a>
                            <a
                                href={`tel:${CLINIC.phoneRaw}`}
                                className="inline-flex items-center gap-2 rounded-full border border-[#3B5249]/30 text-[#3B5249] px-7 py-3.5 text-sm font-medium hover:bg-[#F3F1EC] transition-all"
                                data-testid="hero-call-button"
                            >
                                <Phone className="w-4 h-4" />
                                Call {CLINIC.phone}
                            </a>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="grid grid-cols-3 gap-6 pt-8 border-t border-[#E8E6E1] max-w-xl"
                            data-testid="hero-stats"
                        >
                            {[
                                { k: "10+", v: "Years caring" },
                                { k: "2", v: "Specialties" },
                                { k: "5k+", v: "Smiles restored" },
                            ].map((s) => (
                                <div key={s.v}>
                                    <div className="font-display text-3xl md:text-4xl text-[#3B5249] font-light">
                                        {s.k}
                                    </div>
                                    <div className="text-xs text-[#7A8C84] uppercase tracking-wider mt-1">
                                        {s.v}
                                    </div>
                                </div>
                            ))}
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
                        className="lg:col-span-5 relative"
                    >
                        <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] bg-[#F3F1EC] shadow-[0_30px_80px_rgba(26,34,31,0.15)]">
                            <img
                                src="https://images.unsplash.com/photo-1648775507324-b48dd3791fa5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjbGluaWMlMjBpbnRlcmlvciUyMGJyaWdodHxlbnwwfHx8fDE3Nzc2MzczMTF8MA&ixlib=rb-4.1.0&q=85"
                                alt="Modern bright clinic interior"
                                className="w-full h-full object-cover"
                                data-testid="hero-image"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#1A221F]/20 via-transparent to-transparent" />
                        </div>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8 }}
                            className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-[0_20px_40px_rgba(0,0,0,0.08)] border border-[#E8E6E1] max-w-[220px]"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-2 h-2 rounded-full bg-[#8A9A5B] animate-pulse" />
                                <span className="text-xs uppercase tracking-wider text-[#4A5D54]">
                                    Open today
                                </span>
                            </div>
                            <div className="font-display text-lg text-[#1A221F]">
                                {CLINIC.hours}
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
